import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ReadingsHelpers from 'utils/ReadingsHelpers';

import _ from 'lodash';
import { ResponsiveContainer, LineChart, Line, YAxis, XAxis, CartesianGrid, Tooltip } from 'recharts';
import {
  pink,
  purple,
  deepPurple,
  indigo,
  blue,
  lightBlue,
  cyan,
  teal,
  green,
  lightGreen,
  lime,
  orange,
  blueGrey,
} from '@material-ui/core/colors';
import { Grid, Typography, makeStyles } from '@material-ui/core';
import { CustomGraphDot } from '.';

const colors = [
  green[500],
  pink[400],
  purple['500'],
  teal['500'],
  blue[400],
  indigo['400'],
  deepPurple[500],
  cyan[500],
  lightBlue[500],
  lightGreen[500],
  lime[800],
  orange[700],
  blueGrey[400],
];

function formatUnixTime(tickItem) {
  return moment.unix(tickItem).format('M/D h:mm A');
}

const useStyles = makeStyles(() => ({
  chartTitle: {
    fontWeight: 300,
    color: 'red',
  },
  spacing: {
    height: '50px',
  },
}));

function ReadingGraph({ mobileView, height, dataSet, readingType, convert, syncId, graphDomain }) {
  const { t } = useTranslation();
  const classes = useStyles();

  const tooltipFormatter = (value, name) => {
    if (name === 'ignore') return [];
    return [ReadingsHelpers.displayReading(name, value, '', true, convert), t(name)];
  };

  const yAxisFormatter = (value) => {
    if (name === 'ignore') return [];
    const name = ReadingsHelpers.getReadingName(readingType);
    return ReadingsHelpers.displayReading(name, value, '', false, convert);
  };

  const getColor = (index) => {
    if (index >= colors.length) {
      index = index % colors.length; // eslint-disable-line
    }
    return colors[index];
  };

  const lines = [];
  const first = graphDomain.start;
  const last = graphDomain.end;
  const domain = _.size(dataSet) > 0 ? [first, last] : [0, 0];

  const roundPreviewValue = (value, name) => {
    const rounded = Math.round(100 * value) / 100;
    return tooltipFormatter(rounded, name.split('_')[0]);
  };

  let index = 0;
  _.forEach(readingType, (measurement) => {
    let hasIsolatedPoints = false;
    if (mobileView) {
      _.forEach(dataSet, (dataForReading, i) => {
        if (dataForReading[measurement]) {
          if (!dataSet[i + 1]?.[measurement] && !dataSet[i - 1]?.[measurement]) {
            hasIsolatedPoints = true;
          }
        }
      });
    }

    const regex = new RegExp('^[a-zA-Z]+_[0-9]+$');
    let colorIndex = -1;
    if (regex.test(measurement)) {
      const segments = measurement.split('_');
      const finalSegment = segments[segments.length - 1];
      colorIndex = Number(finalSegment) - 1;
    }

    lines.push(
      <Line
        key={measurement}
        type="monotoneX"
        strokeWidth={2}
        dot={!mobileView || hasIsolatedPoints ? (props) => <CustomGraphDot {...props} /> : false}
        stroke={getColor(colorIndex != -1 ? colorIndex : index)}
        dataKey={measurement}
        name={measurement}
        isAnimationActive={false}
      />
    );
    if (domain[1] - domain[0] < 650000) {
      lines.push(
        <Line
          key={measurement}
          type="monotoneX"
          strokeWidth={1}
          stroke={getColor(colorIndex != -1 ? colorIndex : index)}
          dot={!mobileView || hasIsolatedPoints ? (props) => <CustomGraphDot {...props} /> : false}
          strokeDasharray="2 5"
          dataKey={measurement}
          name="ignore"
          legendType="none"
          isAnimationActive={false}
        />
      );
    }
    index++;
  });

  const referenceLines = [];
  let adjustedHeight = height;

  const hasData = readingType.some((reading) => {
    return dataSet.some((entry) => entry[reading] !== undefined);
  });
  return hasData ? (
    <ResponsiveContainer width="100%" height={adjustedHeight}>
      <LineChart data={dataSet} syncId={syncId}>
        <XAxis
          dataKey="timestamp"
          minTickGap={30}
          tick={{ angle: 60 }}
          tickFormatter={formatUnixTime}
          textAnchor="start"
          height={100}
          type="number"
          scale="time"
          domain={domain}
          interval="preserveStartEnd"
        />
        <CartesianGrid stroke="#eee" strokeDasharray="5 5" />
        {referenceLines}
        <YAxis padding={{ bottom: 20 }} tickFormatter={yAxisFormatter} />
        <Tooltip
          wrapperStyle={{ zIndex: '1' }}
          formatter={roundPreviewValue}
          labelFormatter={formatUnixTime}
          position={{ x: 100, y: 20 }}
        />
        {referenceLines}
        <YAxis padding={{ bottom: 0 }} domain={ReadingsHelpers.getReadingDomain(readingType)} />
        {lines}
      </LineChart>
    </ResponsiveContainer>
  ) : (
    <Grid item className={classes.spacing}>
      <Typography variant="h5" className={classes.chartTitle}>
        {t('no_charts_data')}
      </Typography>
    </Grid>
  );
}

ReadingGraph.propTypes = {
  mobileView: PropTypes.bool,
  dataSet: PropTypes.array.isRequired,
  title: PropTypes.string,
  readingType: PropTypes.array,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showTitle: PropTypes.bool,
  legendClick: PropTypes.func,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  syncId: PropTypes.string,
  convert: PropTypes.bool,
  graphDomain: PropTypes.object,
};

ReadingGraph.defaultProps = {
  mobileView: false,
  showTitle: false,
  height: '95%',
  width: '100%',
  legendClick: undefined,
  title: '',
  convert: true,
  syncId: undefined,
  graphDomain: { start: 0, end: 0 },
};

export default ReadingGraph;
